import { Grid } from "@mui/material";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import StandardAnalyticsChartStatCard from "features/analytics/components/StandardAnalyticsChartStatCard";
import { AnalyticsSpacing } from "consts";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_SITE_OVERVIEW_CHANNEL_PERFORMANCE_PIE,
  CHART_KEY_SITE_OVERVIEW_CONVERSION_TREND,
  CHART_KEY_SITE_OVERVIEW_ENGAGEMENT_TREND,
  CHART_KEY_SITE_OVERVIEW_METRICS_BY_TYPE,
  CHART_KEY_SITE_OVERVIEW_STAT_CONVERSION,
  CHART_KEY_SITE_OVERVIEW_STAT_ENGAGEMENT,
  CHART_KEY_SITE_OVERVIEW_STAT_TRAFFIC,
  CHART_KEY_SITE_OVERVIEW_TOP_CHANNELS,
  CHART_KEY_SITE_OVERVIEW_TRAFFIC_TREND,
  CHART_KEY_SITE_OVERVIEW_VISITORS_BY_TYPE_PIE,
} from "features/analytics/constants/chartKeys";

function SiteOverview() {
  return (
    <Grid container spacing={AnalyticsSpacing} mb={4}>
      <Grid item xs={4}>
        <StandardAnalyticsChartStatCard
          chartKey={CHART_KEY_SITE_OVERVIEW_STAT_TRAFFIC}
          chart={{
            name: "",
            dimension: [ApiDimension.DIMENSION_NOT_SET],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.STAT_CARD,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                ],
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <StandardAnalyticsChartStatCard
          chartKey={CHART_KEY_SITE_OVERVIEW_STAT_ENGAGEMENT}
          chart={{
            name: "",
            dimension: [ApiDimension.DIMENSION_NOT_SET],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.STAT_CARD,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                ],
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <StandardAnalyticsChartStatCard
          chartKey={CHART_KEY_SITE_OVERVIEW_STAT_CONVERSION}
          chart={{
            name: "",
            dimension: [ApiDimension.DIMENSION_NOT_SET],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.STAT_CARD,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
                ],
              },
            ],
            displayOptions: {
              statTitle: "Total Conversions",
              statTitle2: "Conversion Rate",
            },
          }}
        />
      </Grid>

      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_SITE_OVERVIEW_TRAFFIC_TREND}
          chart={{
            name: "Sitewide Traffic Trend",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                ],
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_SITE_OVERVIEW_ENGAGEMENT_TREND}
          chart={{
            name: "Sitewide Engagement Trend",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                ],
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_SITE_OVERVIEW_CONVERSION_TREND}
          chart={{
            name: "Sitewide Conversion Trend",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST],
              },
              {
                key: "r",
                fields: [ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST],
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_SITE_OVERVIEW_CHANNEL_PERFORMANCE_PIE}
          chart={{
            name: "Channel Performance by",
            dimension: [ApiDimension.DIMENSION_CHANNEL],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
              },
            ],
            displayOptions: {
              showTotals: true,
              availableMetrics: [
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_PAGEVIEWS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_PAGEVIEWS_COUNT,
                ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
              ],
            },
          }}
        />
      </Grid>
      <Grid item xs={8} sx={{ height: 450 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_SITE_OVERVIEW_TOP_CHANNELS}
          chart={{
            name: "Top 5 Channels by",
            dimension: [
              ApiDimension.DIMENSION_SESSION_START_BY_DAY,
              ApiDimension.DIMENSION_CHANNEL,
            ],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            limit: 5,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
              },
            ],
            displayOptions: {
              availableMetrics: [
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_PAGEVIEWS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_PAGEVIEWS_COUNT,
                ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
              ],
            },
          }}
        />
      </Grid>
      <Grid item xs={8} sx={{ height: 450 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_SITE_OVERVIEW_METRICS_BY_TYPE}
          chart={{
            name: "Primary Metrics by Type",
            dimension: [ApiDimension.DIMENSION_VISITOR_TYPE],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            limit: 10,
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_LEADS_COUNT,
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_AVERAGE_SESSION_DURATION,
                  ApiMetric.METRIC_AVERAGE_PAGEVIEWS_PER_SESSION,
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
                  ApiMetric.METRIC_LEAD_MACRO_CONVERSION_PERCENTAGE,
                  ApiMetric.METRIC_REVENUE_LAST,
                ],
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_SITE_OVERVIEW_VISITORS_BY_TYPE_PIE}
          chart={{
            name: "Site Visitors by Type",
            dimension: [ApiDimension.DIMENSION_VISITOR_TYPE],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            attributes: [],
            displayOptions: {
              showTotals: true,
              pieTotalText: "Total Leads",
            },
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_LEADS_COUNT],
              },
            ],
          }}
        />
      </Grid>
    </Grid>
  );
}

export default SiteOverview;
